import { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Users, CreditCard, Building2, Calendar, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

type TimeRange = '7d' | '30d' | '90d' | '1y';

interface MetricsData {
  revenue: number;
  studios: number;
  users: number;
  bookings: number;
}

interface SubscriptionDistribution {
  trial: { count: number; percentage: number };
  starter: { count: number; percentage: number };
  professional: { count: number; percentage: number };
  enterprise: { count: number; percentage: number };
}

interface TopStudio {
  name: string;
  owner: string;
  bookings: number;
  revenue: number;
}

const emptyMetrics: Record<TimeRange, MetricsData> = {
  '7d': { revenue: 0, studios: 0, users: 0, bookings: 0 },
  '30d': { revenue: 0, studios: 0, users: 0, bookings: 0 },
  '90d': { revenue: 0, studios: 0, users: 0, bookings: 0 },
  '1y': { revenue: 0, studios: 0, users: 0, bookings: 0 },
};

const emptySubscriptionDistribution: SubscriptionDistribution = {
  trial: { count: 0, percentage: 0 },
  starter: { count: 0, percentage: 0 },
  professional: { count: 0, percentage: 0 },
  enterprise: { count: 0, percentage: 0 },
};

export default function AnalyticsPage() {
  const { getToken } = useAuth();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [metrics, setMetrics] = useState<Record<TimeRange, MetricsData>>(emptyMetrics);
  const [topStudios, setTopStudios] = useState<TopStudio[]>([]);
  const [subscriptionDistribution, setSubscriptionDistribution] = useState<SubscriptionDistribution>(emptySubscriptionDistribution);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/analytics?range=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }
      const data = await response.json();
      if (data.metrics) {
        setMetrics(prev => ({ ...prev, [timeRange]: data.metrics }));
      }
      if (data.topStudios) {
        setTopStudios(data.topStudios);
      }
      if (data.subscriptionDistribution) {
        setSubscriptionDistribution(data.subscriptionDistribution);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [getToken, timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const data = metrics[timeRange];

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-700 dark:text-red-300">Error Loading Data</p>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-stone-900 rounded-xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-6 h-6 text-sage animate-spin" />
            <span className="text-stone-700 dark:text-stone-300">Loading analytics...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 dark:text-stone-100">Analytics</h1>
          <p className="text-stone-500 dark:text-stone-400 mt-1">
            Platform performance and growth metrics
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl p-1">
          {(['7d', '30d', '90d', '1y'] as TimeRange[]).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range
                  ? 'bg-sage text-white'
                  : 'text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800'
              }`}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : range === '90d' ? '90 Days' : '1 Year'}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-sage/10 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-sage" />
            </div>
            <span className="text-sm font-medium text-green-500">+23%</span>
          </div>
          <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">
            ${data.revenue.toLocaleString()}
          </p>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Revenue</p>
        </div>

        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-500" />
            </div>
            <span className="text-sm font-medium text-green-500">+{data.studios}</span>
          </div>
          <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{data.studios}</p>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">New Studios</p>
        </div>

        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-500" />
            </div>
            <span className="text-sm font-medium text-green-500">+{data.users}</span>
          </div>
          <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{data.users}</p>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">New Users</p>
        </div>

        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-amber-50 dark:bg-amber-900/20 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-amber-500" />
            </div>
            <span className="text-sm font-medium text-green-500">+12%</span>
          </div>
          <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">
            {data.bookings.toLocaleString()}
          </p>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Total Bookings</p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart Placeholder */}
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100">Revenue Over Time</h2>
            <TrendingUp className="w-5 h-5 text-sage" />
          </div>
          <div className="h-64 flex items-center justify-center bg-stone-50 dark:bg-stone-800 rounded-xl">
            <p className="text-stone-400">Chart visualization coming soon</p>
          </div>
        </div>

        {/* Signups Chart Placeholder */}
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100">Studio Signups</h2>
            <Building2 className="w-5 h-5 text-blue-500" />
          </div>
          <div className="h-64 flex items-center justify-center bg-stone-50 dark:bg-stone-800 rounded-xl">
            <p className="text-stone-400">Chart visualization coming soon</p>
          </div>
        </div>
      </div>

      {/* Plan Distribution */}
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
        <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100 mb-6">
          Subscription Distribution
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-stone-50 dark:bg-stone-800 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-stone-600 dark:text-stone-400">Trial</span>
              <span className="text-sm text-stone-500">{subscriptionDistribution.trial.percentage}%</span>
            </div>
            <div className="h-2 bg-stone-200 dark:bg-stone-700 rounded-full overflow-hidden">
              <div className="h-full bg-stone-400 rounded-full" style={{ width: `${subscriptionDistribution.trial.percentage}%` }}></div>
            </div>
            <p className="text-lg font-bold text-stone-800 dark:text-stone-100 mt-2">{subscriptionDistribution.trial.count}</p>
          </div>
          <div className="p-4 bg-stone-50 dark:bg-stone-800 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Starter</span>
              <span className="text-sm text-stone-500">{subscriptionDistribution.starter.percentage}%</span>
            </div>
            <div className="h-2 bg-stone-200 dark:bg-stone-700 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${subscriptionDistribution.starter.percentage}%` }}></div>
            </div>
            <p className="text-lg font-bold text-stone-800 dark:text-stone-100 mt-2">{subscriptionDistribution.starter.count}</p>
          </div>
          <div className="p-4 bg-stone-50 dark:bg-stone-800 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-sage">Professional</span>
              <span className="text-sm text-stone-500">{subscriptionDistribution.professional.percentage}%</span>
            </div>
            <div className="h-2 bg-stone-200 dark:bg-stone-700 rounded-full overflow-hidden">
              <div className="h-full bg-sage rounded-full" style={{ width: `${subscriptionDistribution.professional.percentage}%` }}></div>
            </div>
            <p className="text-lg font-bold text-stone-800 dark:text-stone-100 mt-2">{subscriptionDistribution.professional.count}</p>
          </div>
          <div className="p-4 bg-stone-50 dark:bg-stone-800 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Enterprise</span>
              <span className="text-sm text-stone-500">{subscriptionDistribution.enterprise.percentage}%</span>
            </div>
            <div className="h-2 bg-stone-200 dark:bg-stone-700 rounded-full overflow-hidden">
              <div className="h-full bg-purple-500 rounded-full" style={{ width: `${subscriptionDistribution.enterprise.percentage}%` }}></div>
            </div>
            <p className="text-lg font-bold text-stone-800 dark:text-stone-100 mt-2">{subscriptionDistribution.enterprise.count}</p>
          </div>
        </div>
      </div>

      {/* Top Studios */}
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
        <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100 mb-6">
          Top Performing Studios
        </h2>
        <div className="space-y-4">
          {topStudios.map((studio, index) => (
            <div key={`${studio.name}-${index}`} className="flex items-center justify-between py-3 border-b border-stone-100 dark:border-stone-800 last:border-0">
              <div className="flex items-center gap-4">
                <span className="text-lg font-bold text-stone-400 w-6">{index + 1}</span>
                <div>
                  <p className="font-medium text-stone-700 dark:text-stone-300">{studio.name}</p>
                  <p className="text-sm text-stone-500">
                    {studio.owner && <span className="text-stone-400">{studio.owner} · </span>}
                    {studio.bookings} bookings
                  </p>
                </div>
              </div>
              <p className="font-semibold text-sage">${studio.revenue.toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
